import { ArrowRight, Megaphone } from "lucide-react";
import { useSite } from "@/hooks/use-site";
import type { AnnouncementCandidate } from "@/lib/announcement";
import { localizeUrl } from "@/lib/site";

export function AnnouncementBar({ announcement }: { announcement: AnnouncementCandidate | null }) {
  const { site, messages, href } = useSite();
  if (!announcement) return null;

  const copy = announcement.announcement.text ?? announcement.title;
  const postHref = localizeUrl(`/blog/${announcement.slug}`, site.locale, href);
  const accessibleLabel = `${messages.announcementLabel}: ${copy}`;

  return (
    <aside
      aria-label={accessibleLabel}
      className="announcement-bar relative z-[60] border-b text-foreground"
    >
      <a
        href={postHref}
        aria-label={accessibleLabel}
        data-umami-event="announcement-click"
        data-umami-event-story={announcement.storyId}
        data-umami-event-locale={site.locale}
        className="announcement-bar-link group mx-auto flex min-h-11 max-w-7xl items-center justify-center gap-2.5 px-4 py-2 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/50 sm:px-6 sm:text-sm"
      >
        <span className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full border border-primary/15 bg-background/75 px-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-primary">
          <Megaphone aria-hidden className="h-3.5 w-3.5" />
          <span>{messages.announcementLabel}</span>
        </span>
        <span className="line-clamp-1 min-w-0 text-pretty font-semibold leading-5">{copy}</span>
        <ArrowRight
          aria-hidden
          className="h-4 w-4 shrink-0 text-primary transition-transform duration-200 group-hover:translate-x-0.5"
        />
      </a>
    </aside>
  );
}
